import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { safeCopy } from "../lib/utils";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { motion } from "framer-motion";
import {
  Building2,
  Mail,
  Lock,
  Phone,
  User,
  MapPin,
  Loader2,
  CheckCircle2,
  Copy,
  ArrowLeft,
  ShieldCheck,
  ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";
import {
  isFirebaseConfigured,
  sendPhoneOtp,
  friendlyFirebaseError,
  isInsideIframe,
  clearRecaptcha,
} from "../lib/firebase";

const COUNTRY_CODES = [
  { code: "+91", label: "🇮🇳 +91" },
  { code: "+1", label: "🇺🇸 +1" },
  { code: "+44", label: "🇬🇧 +44" },
  { code: "+971", label: "🇦🇪 +971" },
  { code: "+966", label: "🇸🇦 +966" },
  { code: "+65", label: "🇸🇬 +65" },
  { code: "+61", label: "🇦🇺 +61" },
];

export default function Register() {
  const { refreshMe } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    cafe_name: "",
    owner_name: "",
    owner_email: "",
    password: "",
    country_code: "+91",
    phone: "",
    address: "",
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  // ---- Phone OTP verification state ----
  // When Firebase is configured we REQUIRE the owner to verify their phone
  // via SMS OTP before registration is allowed. The verified Firebase ID
  // token is sent with the /auth/register-cafe payload; the backend re-checks
  // it matches the country_code + phone the owner typed.
  const fbReady = isFirebaseConfigured();
  const [otpStep, setOtpStep] = useState("idle"); // "idle" | "sent" | "verified"
  const [otp, setOtp] = useState("");
  const [confirmation, setConfirmation] = useState(null);
  const [idToken, setIdToken] = useState("");
  const [verifyBusy, setVerifyBusy] = useState(false);
  const [verifyProgress, setVerifyProgress] = useState("");
  const [verifyErr, setVerifyErr] = useState("");
  const [recaptchaSeq, setRecaptchaSeq] = useState(0);
  const [cooldown, setCooldown] = useState(0);
  const RESEND_COOLDOWN_SEC = 30;

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);
  useEffect(() => () => clearRecaptcha(), []);

  // If the owner edits the phone after verifying, drop the verification so
  // the backend won't accept a token for a different number.
  const resetVerification = () => {
    setOtpStep("idle");
    setOtp("");
    setConfirmation(null);
    setIdToken("");
    setVerifyErr("");
  };

  const set = (k) => (e) => {
    setForm({ ...form, [k]: e.target.value });
    if ((k === "phone" || k === "country_code") && (otpStep !== "idle" || idToken)) {
      resetVerification();
    }
  };

  const sendOtp = async ({ isResend = false } = {}) => {
    setVerifyErr("");
    const digits = (form.phone || "").replace(/\D/g, "");
    if (!digits) {
      setVerifyErr("Enter your phone number above first.");
      return;
    }
    if (isInsideIframe()) {
      setVerifyErr(
        "Phone OTP doesn't work inside this embedded preview. Open the page in a real browser tab.",
      );
      return;
    }
    setVerifyBusy(true);
    setVerifyProgress(isResend ? "Resending…" : "Sending OTP…");
    try {
      const e164 = `${form.country_code}${digits}`;
      clearRecaptcha();
      const host = document.getElementById("register-recaptcha-host");
      if (host) host.replaceChildren();
      const nextSeq = recaptchaSeq + 1;
      setRecaptchaSeq(nextSeq);
      const nextContainerId = `register-recaptcha-${nextSeq}`;
      const inner = document.createElement("div");
      inner.id = nextContainerId;
      (host || document.body).appendChild(inner);
      const conf = await sendPhoneOtp(e164, {
        onProgress: (m) => setVerifyProgress(m),
        containerId: nextContainerId,
      });
      setConfirmation(conf);
      setOtp("");
      setOtpStep("sent");
      setCooldown(RESEND_COOLDOWN_SEC);
    } catch (e2) {
      setVerifyErr(friendlyFirebaseError(e2));
    } finally {
      setVerifyBusy(false);
      setVerifyProgress("");
    }
  };

  const verifyOtp = async () => {
    setVerifyErr("");
    const code = (otp || "").replace(/\D/g, "");
    if (code.length < 4) {
      setVerifyErr("Enter the 6-digit OTP from your SMS.");
      return;
    }
    if (!confirmation) {
      setVerifyErr("Verification expired — please send a new OTP.");
      setOtpStep("idle");
      return;
    }
    setVerifyBusy(true);
    setVerifyProgress("Verifying…");
    try {
      const result = await confirmation.confirm(code);
      const token = await result.user.getIdToken(true);
      setIdToken(token);
      setOtpStep("verified");
      toast.success("Phone verified");
    } catch (e2) {
      setVerifyErr(friendlyFirebaseError(e2));
    } finally {
      setVerifyBusy(false);
      setVerifyProgress("");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (form.password.length < 6) {
      setErr("Password must be at least 6 characters");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/register-cafe", {
        ...form,
      });
      setDone(data);
      toast.success("Registration submitted — welcome aboard!");
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  const copy = async (t) => {
    const ok = await safeCopy(t);
    if (ok) toast.message("Copied");
    else toast.error("Couldn't copy — please select & copy manually");
  };

  const goIn = async () => {
    // Best-effort refresh so AuthContext picks up the freshly-set cookie.
    // We don't care if it fails (the backend already auto-set the cookie
    // during /auth/register-cafe so the next page load will be authenticated
    // either way). The retry is what guarantees no "session expired" toast.
    try {
      await refreshMe();
    } catch { /* ignore — owner page will refresh anyway */ }
    nav("/owner", { replace: true });
  };

  // Also auto-set "remember" so the user's login persists for a year.
  // Mirrors the backend's `remember=True` default for register-cafe.
  try { window.localStorage.setItem("cl_remember", "1"); } catch { /* ignore */ }

  return (
    <div className="min-h-screen w-full bg-[#0a0f0d] flex items-center justify-center p-6 relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.07]" aria-hidden>
        <div className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full bg-[#10b981] blur-3xl" />
        <div className="absolute -bottom-32 -left-32 w-[480px] h-[480px] rounded-full bg-[#d4af37] blur-3xl" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: "easeOut" }}
        className="relative w-full max-w-xl"
      >
        <Link
          to="/login"
          className="inline-flex items-center gap-1.5 text-xs text-gray-400 hover:text-amber-300 mb-4 uppercase tracking-widest"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to login
        </Link>

        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-[#d4af37] grid place-items-center text-black font-black shadow-lg shadow-amber-500/20">
              8
            </div>
            <span className="text-xs tracking-[0.35em] uppercase text-amber-400/80">
              Cue Genic
            </span>
          </div>
          <h1 className="text-4xl font-black tracking-tight text-white">
            Register your cafe
          </h1>
          <p className="text-gray-400 mt-2 text-sm tracking-wide">
            Your tables will be added by our platform team after we receive your registration.
          </p>
        </div>

        {!done ? (
          <form
            onSubmit={submit}
            className="bg-[#121a16] border border-white/10 rounded-2xl p-7 shadow-[0_30px_60px_rgba(0,0,0,0.5)] backdrop-blur-xl"
            data-testid="register-form"
          >
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.2em] text-gray-400">
                  Cafe Name
                </Label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    data-testid="reg-cafe-name"
                    required
                    value={form.cafe_name}
                    onChange={set("cafe_name")}
                    placeholder="Cue Genic Anna Nagar"
                    className="pl-10 bg-black/40 border-white/10 text-white"
                  />
                </div>
              </div>

              <div className="col-span-2 space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.2em] text-gray-400">
                  Owner Name
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    data-testid="reg-owner-name"
                    required
                    value={form.owner_name}
                    onChange={set("owner_name")}
                    placeholder="Your name"
                    className="pl-10 bg-black/40 border-white/10 text-white disabled:opacity-70"
                  />
                </div>
              </div>

              <div className="col-span-2 space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.2em] text-gray-400">
                  Email
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    data-testid="reg-email"
                    type="email"
                    required
                    value={form.owner_email}
                    onChange={set("owner_email")}
                    placeholder="you@cafe.com"
                    className="pl-10 bg-black/40 border-white/10 text-white disabled:opacity-70"
                  />
                </div>
              </div>

              <div className="col-span-2 space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.2em] text-gray-400">
                  Password (min 6)
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    data-testid="reg-password"
                    type="password"
                    required
                    minLength={6}
                    value={form.password}
                    onChange={set("password")}
                    placeholder="••••••••"
                    className="pl-10 bg-black/40 border-white/10 text-white"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.2em] text-gray-400">
                  Country
                </Label>
                <select
                  data-testid="reg-country-code"
                  value={form.country_code}
                  onChange={set("country_code")}
                  className="w-full h-10 bg-black/40 border border-white/10 text-white rounded-md px-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/60"
                >
                  {COUNTRY_CODES.map((c) => (
                    <option key={c.code} value={c.code} className="bg-[#121a16]">
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.2em] text-gray-400">
                  Phone
                </Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    data-testid="reg-phone"
                    required
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder="9876543210"
                    className="pl-10 bg-black/40 border-white/10 text-white"
                  />
                </div>
                <p className="text-[11px] text-gray-500 tracking-wide">
                  We&apos;ll use this number for account recovery. No SMS verification needed.
                </p>
              </div>

              <div className="col-span-2 space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.2em] text-gray-400">
                  Address (optional)
                </Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    value={form.address}
                    onChange={set("address")}
                    placeholder="Street, City"
                    className="pl-10 bg-black/40 border-white/10 text-white"
                  />
                </div>
              </div>
            </div>

            {err && (
              <div
                data-testid="register-error"
                className="mt-4 text-sm text-red-400 bg-red-500/10 border border-red-500/30 px-3 py-2 rounded-md"
              >
                {err}
              </div>
            )}

            <Button
              type="submit"
              disabled={busy}
              data-testid="register-submit-btn"
              className="w-full h-12 mt-5 bg-[#d4af37] hover:bg-[#e2c258] text-black font-bold tracking-widest uppercase rounded-lg disabled:opacity-60"
            >
              {busy ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                "Register"
              )}
            </Button>
          </form>
        ) : (
          <div
            className="bg-[#121a16] border border-emerald-400/30 rounded-2xl p-7 shadow-2xl"
            data-testid="register-success"
          >
            <div className="text-center mb-5">
              <div className="w-14 h-14 mx-auto rounded-full bg-emerald-500/15 border border-emerald-400/40 grid place-items-center mb-3">
                <CheckCircle2 className="w-7 h-7 text-emerald-300" />
              </div>
              <h2 className="text-2xl font-black tracking-tight">
                Registration Submitted!
              </h2>
              <p className="text-sm text-gray-400 mt-1">
                Your platform owner has been notified and will add your tables
                shortly.
              </p>
            </div>

            <div className="bg-black/40 border border-white/10 rounded-lg p-4">
              <div className="text-[10px] uppercase tracking-[0.25em] text-gray-500 font-bold mb-2">
                Club (Staff) Login — for your tablet
              </div>
              <div className="flex items-center justify-between gap-2 mb-1">
                <code className="text-amber-300 text-xs font-mono break-all">
                  {done.staff_login?.email}
                </code>
                <button
                  onClick={() => copy(done.staff_login?.email)}
                  className="text-gray-400 hover:text-white"
                >
                  <Copy className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="flex items-center justify-between gap-2">
                <code className="text-white text-xs font-mono break-all">
                  {done.staff_login?.password}
                </code>
                <button
                  onClick={() => copy(done.staff_login?.password)}
                  className="text-gray-400 hover:text-white"
                >
                  <Copy className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="text-[11px] text-gray-500 mt-2">
                Use this on the cafe tablet to run tables. Save it now — it
                won&apos;t be shown again.
              </div>
            </div>

            <Button
              onClick={goIn}
              data-testid="enter-console-btn"
              className="w-full h-12 mt-5 bg-amber-500 hover:bg-amber-600 text-black font-bold tracking-widest uppercase"
            >
              Open Owner Console
            </Button>
          </div>
        )}
      </motion.div>
    </div>
  );
}
